import { useRouter } from 'next/router';
import { type ApolloError, useMutation } from '@apollo/client';
import { AcceptApplicationInvitationDocument } from '../../graphql/queries';
import { useEffect } from 'react';
import { TallHeightLoadingMask } from '../../styles/styled-components';
import { getEditRoute } from '../../constants/urls';
import { STEP_IDS } from '../../constants/wizard';
import { Toast, useToast } from '@zillow/constellation';
import { logError } from '@zg-rentals/log-error';

import { useSearchParams } from 'next/navigation';
import { REQUESTING_CLIENT } from '../../constants/api';
import { BRANDS } from '../../constants/brands';

type Props = {
  listingAlias: string;
  applicationId?: string;
  inviteId?: string;
  version?: string;
};

export function AcceptInvite({ listingAlias, applicationId, inviteId, version }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { enqueueToast } = useToast();
  const utmSource = searchParams.get('utm_source') || '';
  const referrerBrand = Object.values(BRANDS).includes(utmSource) ? utmSource : null;
  const utmMedium = searchParams.get('utm_medium') || '';
  const utmGuid = searchParams.get('utm_guid') || '';
  const utmCampaign = searchParams.get('utm_campaign') || '';

  const [acceptInvitationMutation] = useMutation(AcceptApplicationInvitationDocument);

  function tryParseErrorMessage(err: ApolloError): string {
    const errorMessageJson = err.graphQLErrors
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ?.map((x) => (x.extensions?.exception as any)?.responseJson?.message)
      .map((x) => {
        try {
          return JSON.parse(x);
        } catch (e) {
          return undefined;
        }
      })
      .filter((x) => typeof x === 'object')
      .filter((x) => typeof x.responseType === 'string')[0];

    if (errorMessageJson) {
      if (errorMessageJson.responseType == 'WRONG_ZILLOW_ACCOUNT') {
        return 'Wrong Zillow account: ' + errorMessageJson.message;
      }
      if (errorMessageJson.responseType == 'INVITE_DELETED') {
        return errorMessageJson.message;
      }
    }

    return 'We couldn’t add you to this application. Try again.';
  }

  async function acceptInvite() {
    if (inviteId) {
      try {
        const response = await acceptInvitationMutation({
          variables: {
            applicationId,
            listingAlias,
            invitationId: inviteId,
            version,
            requestingClient: REQUESTING_CLIENT,
            utmSource,
            utmMedium,
            utmGuid,
            utmCampaign,
            referrerBrand,
          },
        });
        const createdApplicationId = response.data?.acceptApplicationInvitation?.id;
        if (createdApplicationId) {
          router.push(getEditRoute({ applicationId: createdApplicationId, stepId: STEP_IDS.LEGAL_NAME }));
        }
      } catch (err) {
        logError({
          error: err,
          context: 'AcceptInvite - acceptInvitationMutation error',
        });
        const errorMessage = tryParseErrorMessage(err);
        enqueueToast(<Toast appearance="error" body={errorMessage} />);
        router.push('/');
      }
    }
  }

  useEffect(() => {
    if (listingAlias && inviteId) {
      acceptInvite();
    }
  }, [listingAlias, inviteId]);

  return (
    <TallHeightLoadingMask>
      <></>
    </TallHeightLoadingMask>
  );
}
